import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Package,
  Target,
  Users,
  DollarSign,
  Calendar,
  Star,
  TrendingUp,
  Shield,
  CheckCircle,
  Clock,
  Eye
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';

interface PackageSlot {
  id: string;
  show_id: string;
  show_name: string;
  episode: string;
  slot_type: 'narrative_integration' | 'product_placement';
  rate: number;
  integration_context: string;
  status: 'available' | 'held' | 'assigned';
}

interface PackageShow {
  id: string;
  show_name: string;
  creator: string;
  vertical: string;
  artie_score: number;
  forecasted_reach_90d: number;
  hero_image_url?: string;
  slots: PackageSlot[];
  is_anchor: boolean;
}

interface Package {
  id: string;
  name: string;
  headline: string;
  description: string;
  total_cost: number;
  total_reach: number;
  anchor_shows: PackageShow[];
  fill_shows: PackageShow[];
  brief_alignment: {
    demographic_match: number;
    genre_match: number;
    budget_efficiency: number;
    reach_potential: number;
  };
  suitability_compliance: string[];
  fit_rationale: string;
  created_by: string;
  created_at: string;
  status: 'available' | 'held' | 'assigned';
  hold_expires_at?: string;
}

interface Campaign {
  id: string;
  name: string;
  requirements: {
    budget_usd: number;
    target_ecpm: number;
    target_audience: string[];
    target_categories: string[];
    success_metrics: string[];
  };
}

const ViewPackagePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [packageData, setPackageData] = useState<Package | null>(null);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [holdingPackage, setHoldingPackage] = useState(false);

  const projectId = searchParams.get('project_id');
  const campaignId = searchParams.get('campaign_id');

  useEffect(() => {
    if (projectId && campaignId) {
      fetchPackageData();
    }
  }, [projectId, campaignId]);

  const fetchPackageData = async () => {
    try {
      setLoading(true);
      
      // Fetch campaign details
      const campaignData = await apiClient.get(`/api/v1/campaigns/${campaignId}`) as Campaign;
      setCampaign(campaignData);

      // Fetch package for this project using the new API client method
      const packageData = await apiClient.getBrandMarketplacePackageForProject(projectId!, campaignId!) as Package;
      setPackageData(packageData);

    } catch (error) {
      console.error('Error fetching package data:', error);
      toast({
        title: "Error",
        description: "Failed to load package data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleHoldPackage = async () => {
    if (!packageData || !campaignId) return;

    try {
      setHoldingPackage(true);
      
      const updatedPackage = await apiClient.holdBrandMarketplacePackage(packageData.id, campaignId) as Package;
      setPackageData(updatedPackage);
      
      toast({
        title: "Package Held Successfully",
        description: "This package is now held for your campaign. Hold expires in 48 hours."
      });
    } catch (error) {
      console.error('Error holding package:', error);
      toast({
        title: "Error",
        description: "Failed to hold package. It may already be held by another brand.",
        variant: "destructive"
      });
    } finally {
      setHoldingPackage(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'held': return 'bg-yellow-100 text-yellow-800';
      case 'assigned': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available': return <CheckCircle className="h-4 w-4" />;
      case 'held': return <Clock className="h-4 w-4" />;
      case 'assigned': return <Shield className="h-4 w-4" />;
      default: return <Eye className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!packageData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Package Not Found</h2>
          <p className="text-gray-600 mb-4">The requested package could not be loaded.</p>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const allShows = [...packageData.anchor_shows, ...packageData.fill_shows];
  const totalSlots = allShows.reduce((sum, show) => sum + show.slots.length, 0);
  const averageECPM = packageData.total_cost / (packageData.total_reach / 1000);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Inventory
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{packageData.headline}</h1>
                {campaign && (
                  <p className="text-gray-600">Campaign: {campaign.name}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Badge className={getStatusColor(packageData.status)}>
                {getStatusIcon(packageData.status)}
                <span className="ml-1">{packageData.status.toUpperCase()}</span>
              </Badge>
              {packageData.status === 'available' && (
                <Button onClick={handleHoldPackage} disabled={holdingPackage}>
                  {holdingPackage ? 'Holding...' : 'Hold Package'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Package Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Package Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-6">{packageData.description}</p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{formatCurrency(packageData.total_cost)}</div>
                    <div className="text-sm text-gray-600">Total Cost</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{formatNumber(packageData.total_reach)}</div>
                    <div className="text-sm text-gray-600">Total Reach</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">{totalSlots}</div>
                    <div className="text-sm text-gray-600">Total Slots</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">{formatCurrency(averageECPM)}</div>
                    <div className="text-sm text-gray-600">Avg eCPM</div>
                  </div>
                </div>

                {/* Brief Alignment */}
                <div className="space-y-4">
                  <h4 className="font-medium">Campaign Alignment</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Demographic Match</span>
                        <span>{Math.round(packageData.brief_alignment.demographic_match * 100)}%</span>
                      </div>
                      <Progress value={packageData.brief_alignment.demographic_match * 100} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Genre Match</span>
                        <span>{Math.round(packageData.brief_alignment.genre_match * 100)}%</span>
                      </div>
                      <Progress value={packageData.brief_alignment.genre_match * 100} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Budget Efficiency</span>
                        <span>{Math.round(packageData.brief_alignment.budget_efficiency * 100)}%</span>
                      </div>
                      <Progress value={packageData.brief_alignment.budget_efficiency * 100} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Reach Potential</span>
                        <span>{Math.round(packageData.brief_alignment.reach_potential * 100)}%</span>
                      </div>
                      <Progress value={packageData.brief_alignment.reach_potential * 100} className="h-2" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Anchor Shows */}
            {packageData.anchor_shows.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="h-5 w-5" />
                    Anchor Shows ({packageData.anchor_shows.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {packageData.anchor_shows.map((show) => (
                      <ShowBreakdown key={show.id} show={show} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Fill Shows */}
            {packageData.fill_shows.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Fill Shows ({packageData.fill_shows.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {packageData.fill_shows.map((show) => (
                      <ShowBreakdown key={show.id} show={show} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Fit Rationale */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Why This Package
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">{packageData.fit_rationale}</p>
              </CardContent>
            </Card>

            {/* Suitability Compliance */}
            {packageData.suitability_compliance.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Brand Safety
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {packageData.suitability_compliance.map((compliance, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-sm">{compliance}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Campaign Requirements Match */}
            {campaign && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Requirements Match
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-medium text-sm mb-2">Target Demographics</h4>
                    <div className="flex flex-wrap gap-1">
                      {campaign.requirements.target_audience.map((demo, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {demo.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-sm mb-2">Content Preferences</h4>
                    <div className="flex flex-wrap gap-1">
                      {campaign.requirements.target_categories.map((category, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {category}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Campaign Budget</span>
                      <span className="font-medium">{formatCurrency(campaign.requirements.budget_usd)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Package Cost</span>
                      <span className="font-medium">{formatCurrency(packageData.total_cost)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Remaining Budget</span>
                      <span className={`font-medium ${campaign.requirements.budget_usd - packageData.total_cost >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(campaign.requirements.budget_usd - packageData.total_cost)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Package Details */}
            <Card>
              <CardHeader>
                <CardTitle>Package Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Created by</span>
                  <span className="font-medium">{packageData.created_by}</span>
                </div>
                <div className="flex justify-between">
                  <span>Created</span>
                  <span className="font-medium">
                    {new Date(packageData.created_at).toLocaleDateString()}
                  </span>
                </div>
                {packageData.hold_expires_at && (
                  <div className="flex justify-between">
                    <span>Hold Expires</span>
                    <span className="font-medium text-orange-600">
                      {new Date(packageData.hold_expires_at).toLocaleString()}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// Show Breakdown Component
interface ShowBreakdownProps {
  show: PackageShow;
}

const ShowBreakdown: React.FC<ShowBreakdownProps> = ({ show }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const totalSlotCost = show.slots.reduce((sum, slot) => sum + slot.rate, 0);

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-start gap-4 mb-4">
        {show.hero_image_url && (
          <img 
            src={show.hero_image_url} 
            alt={show.show_name}
            className="w-16 h-16 object-cover rounded"
          />
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium">{show.show_name}</h4>
            {show.is_anchor && (
              <Badge variant="default" className="text-xs">Anchor</Badge>
            )}
          </div>
          <p className="text-sm text-gray-600 mb-2">{show.creator}</p>
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span>Score: {show.artie_score.toFixed(1)}</span>
            <span>Reach: {formatNumber(show.forecasted_reach_90d)}</span>
            <span>Vertical: {show.vertical}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold">{formatCurrency(totalSlotCost)}</div>
          <div className="text-sm text-gray-600">{show.slots.length} slots</div>
        </div>
      </div>

      {/* Slot Breakdown */}
      <div className="space-y-2">
        <h5 className="font-medium text-sm">Slot Breakdown</h5>
        {show.slots.map((slot) => (
          <div key={slot.id} className="flex justify-between items-center text-sm bg-gray-50 p-2 rounded">
            <div>
              <span className="font-medium">{slot.episode}</span>
              <span className="text-gray-600 ml-2">
                {slot.slot_type.replace('_', ' ')}
              </span>
              {slot.integration_context && (
                <span className="text-gray-500 ml-2">- {slot.integration_context}</span>
              )}
            </div>
            <span className="font-medium">{formatCurrency(slot.rate)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ViewPackagePage;